import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

import { PageFilterSummaryItem } from "@/types/page-filters.types";

type PageFiltersContextType = {
    filtersEnabled: boolean;
    filtersOpen: boolean;
    filterSummary: PageFilterSummaryItem[];
    setFiltersOpen: (open: boolean) => void;
    openFilters: () => void;
    setFilterSummary: (summary: PageFilterSummaryItem[]) => void;
    registerPageFilters: () => void;
    unregisterPageFilters: () => void;
};

const PageFiltersContext = createContext<PageFiltersContextType | undefined>(undefined);

export function PageFiltersProvider({ children }: { children: ReactNode }) {
    const [filtersEnabled, setFiltersEnabled] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filterSummary, setFilterSummary] = useState<PageFilterSummaryItem[]>([]);

    const registerPageFilters = useCallback(() => {
        setFiltersEnabled(true);
    }, []);

    const unregisterPageFilters = useCallback(() => {
        setFiltersEnabled(false);
        setFiltersOpen(false);
        setFilterSummary([]);
    }, []);

    const openFilters = useCallback(() => {
        setFiltersOpen(true);
    }, []);

    const value = useMemo(
        () => ({
            filtersEnabled,
            filtersOpen,
            filterSummary,
            setFiltersOpen,
            openFilters,
            setFilterSummary,
            registerPageFilters,
            unregisterPageFilters,
        }),
        [filtersEnabled, filtersOpen, filterSummary, openFilters, registerPageFilters, unregisterPageFilters]
    );

    return <PageFiltersContext.Provider value={value}>{children}</PageFiltersContext.Provider>;
}

export function usePageFilters() {
    const context = useContext(PageFiltersContext);
    if (context === undefined) {
        throw new Error("usePageFilters must be used within a PageFiltersProvider");
    }
    return context;
}
